/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Dice roller component with adaptive animations
 * @version 1.0.0
 * @since v0.0.07
 * @created 2025-12-16
 * @author Agent B (Gameplay/Session/Notifications)
 *
 * Purpose: Interactive dice roller with D&D 5e notation support,
 * context-adaptive animations, and roll history display.
 *
 * CONTRACT REFERENCE:
 * - shared/contracts/dice/v1/dice-rolling.yaml
 *
 * FEATURES:
 * - Text input for notation with validation
 * - Quick buttons for common dice (d4, d6, d8, d10, d12, d20, d100)
 * - Modifier +/- controls
 * - Advantage/Disadvantage toggle for d20
 * - Adaptive animations (minor: 0.5s, standard: 1s, critical: 2.5s)
 * - Roll history with expandable details
 * - WebSocket subscription for broadcast rolls
 *
 * ANIMATION TIERS:
 * - Minor: damage, skill checks (0.5s subtle bounce)
 * - Standard: attacks, saves (1s tumble)
 * - Critical: nat 20/1 on d20 (2.5s dramatic spin with glow)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { webSocketService } from '../../services/websocket/websocketService';

// =============================================================================
// TYPES
// =============================================================================

export type AdvantageState = 'normal' | 'advantage' | 'disadvantage';
export type CriticalStatus = 'none' | 'success' | 'failure';
export type RollImportance = 'minor' | 'standard' | 'critical';

export interface DiceResult {
  sides: number;
  result: number;
  kept: boolean;
}

export interface RollResult {
  notation: string;
  total: number;
  dice: DiceResult[];
  modifier: number;
  advantageState: AdvantageState;
  critical: CriticalStatus;
  timestamp: string;
  sessionId: string;
  playerScreenName: string;
  characterName?: string;
  private: boolean;
  rollId?: string;
}

export interface DiceRollerProps {
  /** Session ID for roll context */
  sessionId: string;
  /** Player's display name */
  playerScreenName: string;
  /** Optional character name */
  characterName?: string;
  /** Callback when a roll is made */
  onRoll?: (result: RollResult) => void;
  /** Disable controls */
  disabled?: boolean;
  /** Optional CSS class */
  className?: string;
  /** Show roll history (default: true) */
  showHistory?: boolean;
  /** Compact mode (default: false) */
  compact?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Common dice types */
const QUICK_DICE = [4, 6, 8, 10, 12, 20, 100] as const;

/** Animation duration by importance (ms) */
const ANIMATION_DURATION: Record<RollImportance, number> = {
  minor: 500,
  standard: 1000,
  critical: 2500
};

/** Notation validation regex */
const NOTATION_REGEX = /^(\d+)d(\d+)(?:(kh|kl|dh|dl)(\d+))?(?:([+-])(\d+))?$/i;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Determine roll importance for animation selection.
 */
function determineRollImportance(result: RollResult): RollImportance {
  // Critical takes precedence
  if (result.critical === 'success' || result.critical === 'failure') {
    return 'critical';
  }
  
  // Check for d20 rolls (attacks, saves are "standard")
  const hasD20 = result.dice.some(d => d.sides === 20);
  if (hasD20) {
    return 'standard';
  }
  
  // Everything else is minor
  return 'minor';
}

/**
 * Validate dice notation format.
 */
function validateNotation(notation: string): { valid: boolean; error?: string } {
  const trimmed = notation.trim().toLowerCase();
  
  if (!trimmed) {
    return { valid: false, error: 'Enter dice notation' };
  }
  
  const match = NOTATION_REGEX.exec(trimmed);
  if (!match) {
    return { valid: false, error: 'Invalid format (e.g., 2d6+5, 4d6kh3)' };
  }
  
  const count = parseInt(match[1], 10);
  const sides = parseInt(match[2], 10);
  const keepDropValue = match[4] ? parseInt(match[4], 10) : null;
  const modifier = match[6] ? parseInt(match[6], 10) : 0;
  
  if (count < 1 || count > 50) {
    return { valid: false, error: 'Dice count: 1-50' };
  }
  if (sides < 1 || sides > 1000) {
    return { valid: false, error: 'Die size: 1-1000' };
  }
  if (modifier > 100) {
    return { valid: false, error: 'Modifier: max ±100' };
  }
  if (keepDropValue !== null && keepDropValue > count) {
    return { valid: false, error: `Can't keep/drop ${keepDropValue} of ${count} dice` };
  }
  
  return { valid: true };
}

// =============================================================================
// COMPONENT
// =============================================================================

export const DiceRoller: React.FC<DiceRollerProps> = ({
  sessionId,
  playerScreenName,
  characterName,
  onRoll,
  disabled = false,
  className = '',
  showHistory = true,
  compact = false
}) => {
  // Input state
  const [notation, setNotation] = useState('1d20');
  const [modifier, setModifier] = useState(0);
  const [advantage, setAdvantage] = useState<AdvantageState>('normal');
  const [isPrivate, setIsPrivate] = useState(false);
  
  // UI state
  const [isRolling, setIsRolling] = useState(false);
  const [lastResult, setLastResult] = useState<RollResult | null>(null);
  const [rollImportance, setRollImportance] = useState<RollImportance>('minor');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [history, setHistory] = useState<RollResult[]>([]);
  const [expandedRoll, setExpandedRoll] = useState<string | null>(null);
  
  const animationTimeoutRef = useRef<number | null>(null);
  
  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);
  
  /**
   * Handle incoming dice roll broadcasts (stable handler for WS subscription)
   */
  const handleDiceRollBroadcast = useCallback((data: any) => {
    // Parse broadcast payload into RollResult
    const result: RollResult = {
      notation: data.notation,
      total: data.total,
      dice: [
        ...data.dice.kept.map((d: any) => ({ ...d, kept: true })),
        ...data.dice.discarded.map((d: any) => ({ ...d, kept: false }))
      ],
      modifier: data.modifier || 0,
      advantageState: data.metadata?.advantage_state || 'normal',
      critical: data.metadata?.critical || 'none',
      timestamp: data.metadata?.timestamp || new Date().toISOString(),
      sessionId: data.metadata?.session_id || sessionId,
      playerScreenName: data.screen_name,
      characterName: data.character_name,
      private: data.metadata?.private || false,
      rollId: data.metadata?.roll_id
    };
    
    // Only show non-private rolls from others (our own are handled locally)
    if (!result.private && result.playerScreenName !== playerScreenName) {
      setHistory(prev => [result, ...prev].slice(0, 50));
    }
  }, [sessionId, playerScreenName]);
  
  // Subscribe to dice roll broadcasts
  useEffect(() => {
    webSocketService.subscribe('dice_roll', handleDiceRollBroadcast);
    return () => {
      webSocketService.unsubscribe('dice_roll', handleDiceRollBroadcast);
    };
  }, [handleDiceRollBroadcast]);
  
  /**
   * Validate notation on change
   */
  useEffect(() => {
    const result = validateNotation(notation);
    setValidationError(result.valid ? null : result.error || null);
  }, [notation]);
  
  /**
   * Build final notation with modifier
   */
  const buildFinalNotation = useCallback((): string => {
    let final = notation.trim().toLowerCase();
    
    // Apply advantage/disadvantage for d20 rolls
    if (advantage !== 'normal' && /^1d20/i.test(final)) {
      final = advantage === 'advantage' ? '2d20kh1' : '2d20kl1';
      // Preserve any existing modifier
      const modMatch = notation.match(/([+-]\d+)$/);
      if (modMatch) {
        final += modMatch[1];
      }
    }
    
    // Apply additional modifier if not already in notation
    if (modifier !== 0 && !notation.includes('+') && !notation.includes('-')) {
      final += modifier > 0 ? `+${modifier}` : `${modifier}`;
    }
    
    return final;
  }, [notation, modifier, advantage]);
  
  /**
   * Execute dice roll
   */
  const handleRoll = useCallback(async () => {
    if (disabled || isRolling || validationError) return;
    
    const finalNotation = buildFinalNotation();
    
    setIsRolling(true);
    
    try {
      // Send roll request to backend
      const sent = webSocketService.send('dice.roll', {
        notation: finalNotation,
        session_id: sessionId,
        player_screen_name: playerScreenName,
        character_name: characterName,
        advantage: advantage,
        private: isPrivate
      });
      
      if (!sent) {
        throw new Error('Failed to send roll request');
      }
      
      // For immediate feedback, we'll listen for the response
      // The actual result comes via the dice_roll subscription
      // Here we just show a placeholder animation
      
      // Simulate receiving result (in production, backend returns this)
      // This is a client-side preview; actual result overwrites it
      const previewResult: RollResult = {
        notation: finalNotation,
        total: 0, // Will be updated
        dice: [],
        modifier: modifier,
        advantageState: advantage,
        critical: 'none',
        timestamp: new Date().toISOString(),
        sessionId,
        playerScreenName,
        characterName,
        private: isPrivate
      };
      
      const importance = determineRollImportance(previewResult);
      setRollImportance(importance);
      
      // Animation timeout
      animationTimeoutRef.current = window.setTimeout(() => {
        setIsRolling(false);
      }, ANIMATION_DURATION[importance]);
      
    } catch (error) {
      console.error('Roll failed:', error);
      setIsRolling(false);
    }
  }, [
    disabled, isRolling, validationError, buildFinalNotation,
    sessionId, playerScreenName, characterName, advantage, isPrivate, modifier
  ]);
  
  /**
   * Handle receiving our own roll result
   */
  const handleOwnRollResult = useCallback((data: any) => {
    const result: RollResult = {
      notation: data.notation,
      total: data.total,
      dice: [
        ...data.dice.kept.map((d: any) => ({ ...d, kept: true })),
        ...data.dice.discarded.map((d: any) => ({ ...d, kept: false }))
      ],
      modifier: data.modifier || 0,
      advantageState: data.metadata?.advantage_state || 'normal',
      critical: data.metadata?.critical || 'none',
      timestamp: data.metadata?.timestamp || new Date().toISOString(),
      sessionId: data.metadata?.session_id || sessionId,
      playerScreenName: data.screen_name,
      characterName: data.character_name,
      private: data.metadata?.private || false,
      rollId: data.metadata?.roll_id
    };
    
    // Update importance based on actual result
    const importance = determineRollImportance(result);
    setRollImportance(importance);
    setLastResult(result);
    setHistory(prev => [result, ...prev].slice(0, 50));
    
    onRoll?.(result);
  }, [sessionId, onRoll]);
  
  // Subscribe to our own roll results
  useEffect(() => {
    const handleResult = (data: any) => {
      if (data.screen_name === playerScreenName) {
        handleOwnRollResult(data);
      }
    };
    
    webSocketService.subscribe('dice_roll', handleResult);
    return () => {
      webSocketService.unsubscribe('dice_roll', handleResult);
    };
  }, [playerScreenName, handleOwnRollResult]);
  
  /**
   * Quick dice button handler
   */
  const handleQuickDice = (sides: number) => {
    setNotation(`1d${sides}`);
  };
  
  /**
   * Modifier adjustment
   */
  const adjustModifier = (delta: number) => {
    setModifier(prev => Math.max(-100, Math.min(100, prev + delta)));
  };
  
  /**
   * Toggle advantage state
   */
  const cycleAdvantage = () => {
    setAdvantage(prev => {
      if (prev === 'normal') return 'advantage';
      if (prev === 'advantage') return 'disadvantage';
      return 'normal';
    });
  };
  
  /**
   * Get animation class based on importance
   */
  const getAnimationClass = (): string => {
    if (!isRolling) return '';
    return `dice-roll-${rollImportance}`;
  };
  
  /**
   * Get critical styling
   */
  const getCriticalClass = (): string => {
    if (!lastResult) return '';
    if (lastResult.critical === 'success') return 'critical-success';
    if (lastResult.critical === 'failure') return 'critical-failure';
    return '';
  };

  return (
    <div className={`dice-roller ${className}`} style={styles.container}>
      {/* CSS for animations */}
      <style>{animationStyles}</style>
      
      {/* Notation Input */}
      <div style={styles.inputSection}>
        <input
          type="text"
          value={notation}
          onChange={(e) => setNotation(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleRoll()}
          placeholder="e.g., 2d6+5, 4d6kh3"
          disabled={disabled}
          style={{
            ...styles.notationInput,
            borderColor: validationError ? '#ef4444' : 'rgba(255,255,255,0.2)'
          }}
          aria-label="Dice notation"
          aria-invalid={!!validationError}
        />
        {validationError && (
          <span style={styles.validationError} role="alert">
            {validationError}
          </span>
        )}
      </div>
      
      {/* Quick Dice Buttons */}
      {!compact && (
        <div style={styles.quickDice} role="group" aria-label="Quick dice selection">
          {QUICK_DICE.map((sides) => (
            <button
              key={sides}
              onClick={() => handleQuickDice(sides)}
              disabled={disabled}
              style={{
                ...styles.quickButton,
                ...(notation === `1d${sides}` ? styles.quickButtonActive : {})
              }}
              aria-label={`Roll 1d${sides}`}
              title={`d${sides}`}
            >
              d{sides}
            </button>
          ))}
        </div>
      )}
      
      {/* Modifier & Options Row */}
      <div style={styles.optionsRow}>
        {/* Modifier Controls */}
        <div style={styles.modifierControls}>
          <button
            onClick={() => adjustModifier(-1)}
            disabled={disabled || modifier <= -100}
            style={styles.modButton}
            aria-label="Decrease modifier"
          >
            −
          </button>
          <span style={styles.modifierValue}>
            {modifier >= 0 ? `+${modifier}` : modifier}
          </span>
          <button
            onClick={() => adjustModifier(1)}
            disabled={disabled || modifier >= 100}
            style={styles.modButton}
            aria-label="Increase modifier"
          >
            +
          </button>
        </div>
        
        {/* Advantage Toggle (for d20) */}
        <button
          onClick={cycleAdvantage}
          disabled={disabled}
          style={{
            ...styles.advantageButton,
            backgroundColor: advantage === 'normal' ? 'rgba(255,255,255,0.1)' :
              advantage === 'advantage' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'
          }}
          aria-label={`Advantage: ${advantage}`}
          title={`Click to cycle: ${advantage}`}
        >
          {advantage === 'normal' && '⚖️'}
          {advantage === 'advantage' && '⬆️ Adv'}
          {advantage === 'disadvantage' && '⬇️ Dis'}
        </button>
        
        {/* Private Roll Toggle */}
        <button
          onClick={() => setIsPrivate(!isPrivate)}
          disabled={disabled}
          style={{
            ...styles.privateButton,
            backgroundColor: isPrivate ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.1)'
          }}
          aria-pressed={isPrivate}
          aria-label={isPrivate ? 'Private roll (MM only)' : 'Public roll'}
          title={isPrivate ? 'Roll visible to MM only' : 'Roll visible to all'}
        >
          {isPrivate ? '🔒' : '🔓'}
        </button>
      </div>
      
      {/* Roll Button */}
      <button
        onClick={handleRoll}
        disabled={disabled || isRolling || !!validationError}
        style={{
          ...styles.rollButton,
          opacity: (disabled || isRolling || validationError) ? 0.6 : 1
        }}
        aria-label="Roll dice"
      >
        <span className={getAnimationClass()} style={styles.diceIcon}>
          🎲
        </span>
        {isRolling ? 'Rolling...' : 'Roll'}
      </button>
      
      {/* Result Display */}
      {lastResult && (
        <div 
          style={styles.resultDisplay}
          className={getCriticalClass()}
          role="status"
          aria-live="polite"
        >
          <div style={styles.resultTotal} className={getCriticalClass()}>
            {lastResult.total}
          </div>
          <div style={styles.resultDetails}>
            <span style={styles.resultNotation}>{lastResult.notation}</span>
            <span style={styles.resultBreakdown}>
              [{lastResult.dice.filter(d => d.kept).map(d => d.result).join(', ')}]
              {lastResult.modifier !== 0 && (
                <span> {lastResult.modifier > 0 ? '+' : ''}{lastResult.modifier}</span>
              )}
            </span>
            {lastResult.critical !== 'none' && (
              <span style={{
                ...styles.criticalBadge,
                backgroundColor: lastResult.critical === 'success' ? 
                  'rgba(234,179,8,0.3)' : 'rgba(239,68,68,0.3)',
                color: lastResult.critical === 'success' ? '#fbbf24' : '#f87171'
              }}>
                {lastResult.critical === 'success' ? '⭐ CRITICAL!' : '💀 FUMBLE!'}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Roll History */}
      {showHistory && history.length > 0 && (
        <div style={styles.historySection}>
          <h4 style={styles.historyTitle}>Recent Rolls</h4>
          <div style={styles.historyList}>
            {history.slice(0, 10).map((roll, idx) => (
              <div
                key={roll.rollId || `${roll.timestamp}-${idx}`}
                style={styles.historyItem}
                onClick={() => setExpandedRoll(
                  expandedRoll === roll.rollId ? null : roll.rollId || null
                )}
                role="button"
                tabIndex={0}
                aria-expanded={expandedRoll === roll.rollId}
              >
                <div style={styles.historyMain}>
                  <span style={styles.historyPlayer}>
                    {roll.characterName || roll.playerScreenName}
                  </span>
                  <span style={styles.historyNotation}>{roll.notation}</span>
                  <span style={{
                    ...styles.historyTotal,
                    color: roll.critical === 'success' ? '#fbbf24' :
                      roll.critical === 'failure' ? '#f87171' : '#fff'
                  }}>
                    = {roll.total}
                  </span>
                  {roll.private && <span style={styles.privateBadge}>🔒</span>}
                </div>
                {expandedRoll === roll.rollId && (
                  <div style={styles.historyExpanded}>
                    <span>Dice: [{roll.dice.map(d => 
                      `${d.result}${d.kept ? '' : '̶'}`
                    ).join(', ')}]</span>
                    {roll.modifier !== 0 && <span> Mod: {roll.modifier}</span>}
                    <span style={styles.historyTime}>
                      {new Date(roll.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: '"Cinzel", serif',
    maxWidth: '320px'
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  notationInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    color: '#fff',
    fontSize: '16px',
    fontFamily: '"Cinzel", serif',
    textAlign: 'center'
  },
  validationError: {
    fontSize: '11px',
    color: '#f87171',
    textAlign: 'center'
  },
  quickDice: {
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
    flexWrap: 'wrap'
  },
  quickButton: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif'
  },
  quickButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    borderColor: '#8b5cf6',
    color: '#fff'
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px'
  },
  modifierControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  modButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modifierValue: {
    minWidth: '40px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#fff'
  },
  advantageButton: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: '"Cinzel", serif'
  },
  privateButton: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer'
  },
  rollButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif'
  },
  diceIcon: {
    fontSize: '20px',
    display: 'inline-block'
  },
  resultDisplay: {
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    textAlign: 'center'
  },
  resultTotal: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '8px'
  },
  resultDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  resultNotation: {
    fontFamily: 'monospace'
  },
  resultBreakdown: {
    fontFamily: 'monospace'
  },
  criticalBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    marginTop: '4px',
    display: 'inline-block'
  },
  historySection: {
    marginTop: '8px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: '12px'
  },
  historyTitle: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0 8px 0'
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  historyItem: {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    cursor: 'pointer',
    fontSize: '12px'
  },
  historyMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  historyPlayer: {
    color: 'rgba(255, 255, 255, 0.6)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  historyNotation: {
    fontFamily: 'monospace',
    color: 'rgba(255, 255, 255, 0.8)'
  },
  historyTotal: {
    fontWeight: '600'
  },
  privateBadge: {
    fontSize: '10px'
  },
  historyExpanded: {
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '11px'
  },
  historyTime: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.3)'
  }
};

/** CSS animation keyframes */
const animationStyles = `
  /* Minor roll - subtle bounce (0.5s) */
  @keyframes dice-roll-minor {
    0% { transform: rotate(0deg) scale(1); }
    50% { transform: rotate(180deg) scale(1.1); }
    100% { transform: rotate(360deg) scale(1); }
  }
  
  /* Standard roll - tumble (1s) */
  @keyframes dice-roll-standard {
    0% { transform: rotate(0deg) translateY(0); }
    25% { transform: rotate(90deg) translateY(-10px); }
    50% { transform: rotate(180deg) translateY(0); }
    75% { transform: rotate(270deg) translateY(-5px); }
    100% { transform: rotate(360deg) translateY(0); }
  }
  
  /* Critical roll - dramatic spin with glow (2.5s) */
  @keyframes dice-roll-critical {
    0% { transform: rotate(0deg) scale(1); filter: brightness(1); }
    25% { transform: rotate(180deg) scale(1.2); filter: brightness(1.2); }
    50% { transform: rotate(540deg) scale(1.3); filter: brightness(1.5); }
    75% { transform: rotate(900deg) scale(1.1); filter: brightness(1.3); }
    100% { transform: rotate(1080deg) scale(1); filter: brightness(1); }
  }
  
  .dice-roll-minor {
    animation: dice-roll-minor 0.5s ease-in-out;
  }
  
  .dice-roll-standard {
    animation: dice-roll-standard 1s ease-in-out;
  }
  
  .dice-roll-critical {
    animation: dice-roll-critical 2.5s ease-in-out;
  }
  
  .critical-success {
    color: #fbbf24;
    text-shadow: 0 0 10px rgba(251, 191, 36, 0.5);
  }
  
  .critical-failure {
    color: #f87171;
    text-shadow: 0 0 10px rgba(248, 113, 113, 0.5);
  }
`;

export default DiceRoller;

